import type { BackupEntry } from '@/mocks/saGlobalBackupCenter';
import { statusMeta, typeMeta } from '@/mocks/saGlobalBackupCenter';

interface BackupTableProps {
  entries: BackupEntry[];
  onSelect: (entry: BackupEntry) => void;
}

const actionIcons: Record<string, string> = {
  success: 'ri-checkbox-circle-fill text-emerald-500',
  in_progress: 'ri-loader-4-fill text-amber-500 animate-spin',
  failed: 'ri-close-circle-fill text-red-500',
  pending: 'ri-time-fill text-sky-500',
};

export default function BackupTable({ entries, onSelect }: BackupTableProps) {
  if (entries.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
          <i className="ri-archive-line text-foreground-400 text-2xl"></i>
        </div>
        <p className="text-foreground-600 text-sm font-semibold">Sin resultados</p>
        <p className="text-foreground-400 text-xs mt-1">No se encontraron backups con los filtros actuales.</p>
      </div>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              {['Nombre', 'Tipo', 'Estado', 'Tamaño', 'Fecha', 'Servidor', 'Duración', 'Base de Datos', ''].map((h) => (
                <th key={h} className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const st = statusMeta[entry.status];
              const tp = typeMeta[entry.type];
              return (
                <tr
                  key={entry.id}
                  className="border-b border-background-200/60 hover:bg-background-100/60 transition-colors cursor-pointer"
                  onClick={() => onSelect(entry)}
                >
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-800 text-xs font-semibold whitespace-nowrap">{entry.id}</span>
                      <span className="text-foreground-500 text-[11px] truncate max-w-[180px] hidden lg:inline">{entry.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-200 text-foreground-600 text-[10px] font-medium whitespace-nowrap">
                      <i className={`${tp.icon} text-[10px]`}></i>
                      {tp.label}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${st.bg} ${st.color} text-[10px] font-semibold whitespace-nowrap`}>
                      <i className={`${actionIcons[entry.status]} text-[9px]`}></i>
                      {st.label}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-foreground-700 text-xs font-mono whitespace-nowrap">{entry.sizeLabel}</span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col">
                      <span className="text-foreground-700 text-xs whitespace-nowrap">
                        {new Date(entry.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                      <span className="text-foreground-400 text-[10px]">
                        {new Date(entry.date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-foreground-600 text-[11px] font-mono whitespace-nowrap">{entry.server}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-foreground-600 text-[11px] whitespace-nowrap">{entry.durationLabel}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-foreground-600 text-[11px] font-mono whitespace-nowrap">{entry.database}</span>
                  </td>
                  <td className="px-3 py-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); onSelect(entry); }}
                      className="w-8 h-8 rounded-lg bg-background-200 hover:bg-background-300 flex items-center justify-center transition-colors cursor-pointer"
                      title="Ver detalle"
                    >
                      <i className="ri-arrow-right-s-line text-foreground-500 text-sm"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}